import React, { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Package, Sparkles, Calendar } from "lucide-react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { ACCOUNT } from "@/constants/testIds";
import { toast } from "sonner";
import { OrderRow, KundliRow, BookingRow, EmptyState } from "./account/AccountRows";

const Account = () => {
  const { user, authLoading } = useApp();
  const [orders, setOrders] = useState([]);
  const [kundlis, setKundlis] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [o, k, b] = await Promise.allSettled([
      api.get("/me/orders"),
      api.get("/me/kundlis"),
      api.get("/me/bookings"),
    ]);
    if (o.status === "fulfilled") setOrders(o.value.data);
    if (k.status === "fulfilled") setKundlis(k.value.data);
    if (b.status === "fulfilled") setBookings(b.value.data);
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const deleteKundli = useCallback(async (id) => {
    try {
      await api.delete(`/me/kundlis/${id}`);
      toast.success("Reading removed");
      setKundlis((k) => k.filter((x) => x.id !== id));
    } catch { toast.error("Failed"); }
  }, []);

  const cancelBooking = useCallback(async (b) => {
    if (!window.confirm(`Cancel consultation with ${b.astrologer_name}?`)) return;
    try {
      const r = await api.delete(`/me/bookings/${b.session_id}`);
      toast.success(r.data.message || "Cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }, [load]);

  const reschedule = useCallback(async (booking, { date, time }) => {
    if (!date || !time) { toast.error("Pick date and time"); return; }
    setSaving(true);
    try {
      await api.patch(`/me/bookings/${booking.session_id}`, { new_slot: `${date}T${time}` });
      toast.success("Consultation rescheduled");
      setRescheduleFor(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reschedule failed");
    }
    setSaving(false);
  }, [load]);

  const toggleReschedule = useCallback((sessionId) => {
    setRescheduleFor((prev) => (prev === sessionId ? null : sessionId));
  }, []);

  if (authLoading) return (
    <div className="max-w-6xl mx-auto px-6 py-24 text-muted-foreground text-sm uppercase tracking-label">
      Loading…
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div data-testid={ACCOUNT.page} className="max-w-6xl mx-auto px-6 sm:px-10 py-16">
      <p className="text-xs uppercase tracking-label text-secondary">My Account</p>
      <h1 className="display-serif text-5xl mt-3">Namaste, {user.name.split(" ")[0]}.</h1>
      <p className="mt-3 text-muted-foreground">{user.email}</p>

      <Tabs defaultValue="orders" className="mt-12">
        <TabsList className="grid grid-cols-3 max-w-lg bg-muted p-1 rounded-none">
          <TabsTrigger value="orders" data-testid={ACCOUNT.tabOrders} className="uppercase tracking-label text-xs data-[state=active]:bg-background rounded-none">Orders</TabsTrigger>
          <TabsTrigger value="kundlis" data-testid={ACCOUNT.tabKundlis} className="uppercase tracking-label text-xs data-[state=active]:bg-background rounded-none">Saved Kundlis</TabsTrigger>
          <TabsTrigger value="bookings" data-testid={ACCOUNT.tabBookings} className="uppercase tracking-label text-xs data-[state=active]:bg-background rounded-none">Consultations</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-8">
          <div data-testid={ACCOUNT.ordersList} className="space-y-4">
            {orders.length === 0 && (
              <EmptyState icon={Package} title="No orders yet"
                cta={<Link to="/store" className="text-primary underline-offset-4 hover:underline">Browse the store →</Link>} />
            )}
            {orders.map((o) => <OrderRow key={o.session_id} order={o} />)}
          </div>
        </TabsContent>

        <TabsContent value="kundlis" className="mt-8">
          <div data-testid={ACCOUNT.kundlisList} className="space-y-4">
            {kundlis.length === 0 && (
              <EmptyState icon={Sparkles} title="No saved readings"
                cta={<Link to="/predictions" className="text-primary underline-offset-4 hover:underline">Generate your Kundli →</Link>} />
            )}
            {kundlis.map((k) => <KundliRow key={k.id} kundli={k} onDelete={deleteKundli} />)}
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="mt-8">
          <div data-testid={ACCOUNT.bookingsList} className="space-y-4">
            {bookings.length === 0 && (
              <EmptyState icon={Calendar} title="No consultations booked"
                cta={<Link to="/consult" className="text-primary underline-offset-4 hover:underline">Book an astrologer →</Link>} />
            )}
            {bookings.map((b) => (
              <BookingRow
                key={b.session_id}
                booking={b}
                isRescheduling={rescheduleFor === b.session_id}
                onToggleReschedule={toggleReschedule}
                onReschedule={reschedule}
                onCancel={cancelBooking}
                saving={saving}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Account;
